/**
 * One skill's run history and metrics. Polls while a run is still `running`
 * so the Runs tab follows a "Run on this case" started a moment ago. `rate`
 * records the attorney's thumbs and note on a run.
 */
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";

import type { FirmSkillRun, SkillMetrics } from "../types";

const POLL_MS = 5000;

export const EMPTY_METRICS: SkillMetrics = {
  runs: 0,
  completed: 0,
  failed: 0,
  needs_attention: 0,
  thumbs_up: 0,
  thumbs_down: 0,
  last_run_at: null,
  last_status: null
};

export type SkillRunsState = {
  runs: FirmSkillRun[];
  metrics: SkillMetrics;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  rate: (
    runId: string,
    rating: "up" | "down" | "",
    note?: string
  ) => Promise<FirmSkillRun>;
};

export const useSkillRuns = (
  skillId: string | null,
  scope: FirmScope
): SkillRunsState => {
  const [runs, setRuns] = useState<FirmSkillRun[]>([]);
  const [metrics, setMetrics] = useState<SkillMetrics>(EMPTY_METRICS);
  const [loading, setLoading] = useState(Boolean(skillId));
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  const scopeKey = scope.lawFirmId ?? "";
  const apiScope = useMemo<FirmScope>(
    () => (scopeKey ? { lawFirmId: scopeKey } : {}),
    [scopeKey]
  );

  const refresh = useCallback(async () => {
    if (!skillId) {
      setRuns([]);
      setMetrics(EMPTY_METRICS);
      setLoading(false);
      return;
    }
    try {
      const page = await firmKnowledgeApi.listSkillRuns(skillId, apiScope);
      if (!alive.current) return;
      setRuns(page.runs);
      setMetrics(page.metrics ?? EMPTY_METRICS);
      setError(null);
    } catch (e) {
      Sentry.captureException(e);
      if (!alive.current) return;
      setError(e instanceof Error ? e.message : "Could not load runs.");
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [apiScope, skillId]);

  useEffect(() => {
    alive.current = true;
    setLoading(Boolean(skillId));
    void refresh();
    return () => {
      alive.current = false;
    };
  }, [refresh, skillId]);

  const running = runs.some((r) => r.status === "running");
  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [running, refresh]);

  const rate = useCallback(
    async (runId: string, rating: "up" | "down" | "", note?: string) => {
      if (!skillId) throw new Error("No automation selected.");
      const updated = await firmKnowledgeApi.rateSkillRun(
        skillId,
        runId,
        { rating, note },
        apiScope
      );
      if (alive.current) {
        setRuns((prev) => prev.map((r) => (r.id === runId ? updated : r)));
        void refresh();
      }
      return updated;
    },
    [apiScope, refresh, skillId]
  );

  return { runs, metrics, loading, error, refresh, rate };
};
